// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  Share,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { resolveTourInviteLink } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';

type ShareLinkButtonProps = {
  // Tour the shareable invite link is generated for. The button hides itself when
  // there is no tour in context (e.g. anonymous invite view or a tour-less round).
  tourId?: string | null;
  style?: StyleProp<ViewStyle>;
};

/**
 * Top-of-page "Share link" button. Opens the same invite-share modal used on the
 * tournaments and round-start screens: it resolves the tour invite link via the
 * BFF and presents the join code, a QR code, and a Share action (native share
 * sheet, or the Web Share API / clipboard fallback on web).
 */
export function ShareLinkButton({ tourId, style }: ShareLinkButtonProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const joinCode = useMemo(() => {
    if (!link) {
      return null;
    }
    const token = decodeURIComponent(link.split('/').pop() ?? '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim()
      .toUpperCase();
    if (!token) {
      return null;
    }
    return token.length > 6 ? token.slice(0, 6) : token;
  }, [link]);

  // After every hook: an early return above a hook makes the hook count differ
  // between renders once tourId resolves (null on mount, set by an effect on the
  // tour leaderboard), which crashes React with "Rendered more hooks than during
  // the previous render" and trips the app error boundary.
  if (!tourId) {
    return null;
  }

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setLink(null);
    try {
      const inviteLink = await resolveTourInviteLink({ apiBaseUrl, tourId, authToken: user?.token });
      setLink(inviteLink);
    } catch {
      setError(t('tournaments.shareLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!link) {
      return;
    }
    const title = t('tournaments.shareAction');
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const nav = navigator as {
          share?: (data: { title?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };
        if (typeof nav.share === 'function') {
          await nav.share({ title, url: link });
          return;
        }
        if (nav.clipboard?.writeText) {
          await nav.clipboard.writeText(link);
          setError(null);
          return;
        }
        throw new Error('share unsupported');
      }

      await Share.share({ title, message: link });
    } catch (err) {
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('cancel')) {
        return; // user dismissed the share sheet
      }
      setError(t('tournaments.shareSendError'));
    }
  };

  return (
    <View style={[styles.row, style]}>
      <GoffyButton
        label={t('tournaments.shareAction')}
        onPress={() => void handleOpen()}
        tone="accent"
        size="compact"
        accessibilityLabel={t('tournaments.shareAction')}
      />

      <Modal transparent visible={open} animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.shareOverlay} onPress={() => setOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>{t('players.inviteOthers')}</Text>
            <View style={styles.shareCodeCard}>
              <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
              <Text style={styles.shareCodeValue}>{joinCode || '--- ---'}</Text>
              {loading ? <ActivityIndicator size="small" color={appColors.accent.primary} /> : null}
              {link ? (
                <View style={styles.shareQrWrapper}>
                  <QRCode value={link} size={220} color="#000000" backgroundColor="#ffffff" />
                </View>
              ) : null}
            </View>
            {error ? <Text style={styles.error}>{error}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShare()}
                disabled={!link || loading}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setOpen(false)}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    width: '100%',
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#04210D',
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: '#AEE79A',
    fontSize: 48,
    letterSpacing: 6,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  shareActions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    width: '100%',
  },
  error: {
    color: '#ff7b7b',
    marginTop: 4,
  },
});
